// src/components/Navbar.jsx
// Shared navbar — import this in every page, remove the per-page nav code.
//
// Usage:
//   import Navbar from "../components/Navbar";
//   <Navbar activePage="Fleet" />
//
// activePage must match one of the label strings below exactly.

import { useState } from "react";
import { useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home",               href: "/" },
  { label: "Fleet",              href: "/our-cars" },
  { label: "Transfer",            href: "/transfer" },
  { label: "Modify Reservation", href: "/modify-reservetion" },
  { label: "About",              href: "/about-us" },
  { label: "Gallery",            href: "/gallery" },
  { label: "FAQ",                href: "/faqs" },
  { label: "Contact",            href: "/contact-us" },
  
];

const navCss = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold:       #C9A84C;
    --gold-light: #E8C97A;
    --dark:       #0A0A0F;
    --dark2:      #111118;
    --surface:    #16161F;
    --border:     rgba(201,168,76,0.18);
    --text:       #E8E8F0;
    --muted:      #7A7A9A;
    --white:      #FFFFFF;
  }

  /* ── DESKTOP NAV ── */
  .navbar {
    position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 5%; height: 72px;
    background: rgba(10,10,15,0.96);
    backdrop-filter: blur(18px);
    border-bottom: 1px solid var(--border);
    font-family: 'DM Sans', sans-serif;
  }

  .navbar-logo {
    font-family: 'Playfair Display', serif;
    font-size: 1.6rem; color: var(--gold);
    letter-spacing: -0.5px; cursor: pointer;
    text-decoration: none;
  }
  .navbar-logo span { color: var(--white); }

  .navbar-links {
    display: flex; gap: 2rem; list-style: none; margin: 0; padding: 0;
  }
  .navbar-links a {
    color: var(--muted); text-decoration: none;
    font-size: .9rem; font-weight: 500; letter-spacing: .5px;
    transition: color .2s; white-space: nowrap;
  }
  .navbar-links a:hover,
  .navbar-links a.active { color: var(--gold); }

  .navbar-cta {
    background: var(--gold); color: var(--dark);
    font-weight: 600; font-size: .85rem;
    padding: .55rem 1.4rem; border: none; border-radius: 8px;
    cursor: pointer; font-family: inherit; text-decoration: none;
    transition: background .2s; white-space: nowrap;
    display: inline-block; line-height: normal;
  }
  .navbar-cta:hover { background: var(--gold-light); }

  /* ── HAMBURGER ── */
  .navbar-toggle {
    display: none; flex-direction: column; gap: 5px;
    cursor: pointer; background: none; border: none; padding: 4px;
  }
  .navbar-toggle span {
    display: block; width: 24px; height: 2px;
    background: var(--gold); border-radius: 2px; transition: all .3s;
  }

  /* ── MOBILE DROPDOWN ── */
  .navbar-mobile {
    display: none;
    position: fixed; top: 72px; left: 0; right: 0; z-index: 999;
    background: rgba(10,10,15,0.98); backdrop-filter: blur(18px);
    border-bottom: 1px solid var(--border);
    padding: 1.5rem 5%; flex-direction: column;
    font-family: 'DM Sans', sans-serif;
  }
  .navbar-mobile.open { display: flex; }
  .navbar-mobile a {
    color: var(--muted); text-decoration: none;
    font-size: 1rem; font-weight: 500;
    padding: .9rem 0; border-bottom: 1px solid var(--border);
    display: block; transition: color .2s;
  }
  .navbar-mobile a:hover,
  .navbar-mobile a.active { color: var(--gold); }
  .navbar-mobile .navbar-cta {
    margin-top: 1rem; width: 100%; text-align: center;
    padding: .7rem; font-size: .95rem; display: block;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .navbar-links,
    .navbar > .navbar-cta { display: none; }
    .navbar-toggle { display: flex; }
  }
`;

export default function Navbar({ activePage = "", siteName = "" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const go = (href) => {
    navigate(href);
    setMenuOpen(false);
  };

  return (
    <>
      <style>{navCss}</style>

      {/* ── DESKTOP ── */}
      <nav className="navbar">
        <span className="navbar-logo" onClick={() => go("/")}>
          Orisnz<span>{siteName?.replace("Drive", "") || "-Fleet"}</span>
        </span>

        <ul className="navbar-links">
          {NAV_LINKS.map(link => (
            <li key={link.label}>
              <a
                href={link.href}
                className={activePage === link.label ? "active" : ""}
                onClick={e => { e.preventDefault(); go(link.href); }}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <a className="navbar-cta" href="/#bknengine" onClick={e => { e.preventDefault(); go("/#bknengine"); }}>
          Book Now
        </a>

        {/* Hamburger */}
        <button
          className="navbar-toggle"
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span style={menuOpen ? { transform: "rotate(45deg) translate(5px,5px)" } : {}} />
          <span style={menuOpen ? { opacity: 0 }                                   : {}} />
          <span style={menuOpen ? { transform: "rotate(-45deg) translate(5px,-5px)" } : {}} />
        </button>
      </nav>

      {/* ── MOBILE DROPDOWN ── */}
      <div className={`navbar-mobile${menuOpen ? " open" : ""}`}>
        {NAV_LINKS.map(link => (
          <a
            key={link.label}
            href={link.href}
            className={activePage === link.label ? "active" : ""}
            onClick={e => { e.preventDefault(); go(link.href); }}
          >
            {link.label}
          </a>
        ))}
        <a className="navbar-cta" href="/#bknengine" onClick={e => { e.preventDefault(); go("/#bknengine"); }}>
          Book Now
        </a>
      </div>
    </>
  );
}